import { CheckCircleFilled, PrinterOutlined, WarningOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Col, Empty, Flex, List, Progress, Radio, Row, Segmented, Space, Table, Tag, Typography } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { api, unwrap, type Schemas } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { ScanInput } from "@/components/ScanInput";
import { useI18n } from "@/i18n";
import { SkuThumb } from "@/components/SkuThumb";
import type { ScanResult } from "@/lib/scanSound";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { ShipBy } from "../orders/ShipBy";
import { CourierKindTag } from "../orders/CourierKind";
import { MethodTag, NoWms, WaveOrderTag, useWmsWarehouse, type Wave } from "./common";
import { usePackLabel } from "./packLabel";
import { AgentCamera, PackingCamera, useVideoUploads } from "./PackingCamera";
import { useAgentRecorder } from "./agent";
import { usePackingRecorder, type Segment } from "./recorder";
import { useTailedRecorder, type TailedRecorder } from "./tailedRecorder";
import { usePoll } from "@/components/tabs/WorkTabs";

type PackOrder = Schemas["PackOrder"];
type Packaging = Schemas["Packaging"];
type PackingWave = Schemas["PackingWave"];
type WaveOrder = NonNullable<Wave["orderList"]>[number];
type Mode = "single" | "order";
type Contents = Awaited<ReturnType<typeof scanContainer>>;
// Totes and carts are numbered by Zerpid: TOTE-0001, CART-01, CART-01-05.
const CONTAINER = /^(TOTE|CART)-/i;
function scanContainer(warehouseId: string, code: string) {
  return unwrap(api.GET("/v1/warehouses/{warehouseID}/containers/{code}", { params: { path: { warehouseID: warehouseId, code: code.toUpperCase() } } }));
}
/** What the desk records with, when the warehouse records packing. */
type Desk = {
  rec: TailedRecorder | null;
  push: (orderId: string, externalId: string, seg: Segment) => void;
  // Before a parcel closes: false when the packer stops because it would not be recorded.
  confirm: () => Promise<boolean>;
};

/**
 * Gudang > Packing (Plan §6.9). Single-pcs: one scan packs the next order that
 * needs the item and prints its label. Per order (batch, cluster): open an
 * order, scan each item, close the parcel in the suggested box.
 */
export function Packing() {
  const { t, tDynamic } = useI18n();
  const { modal } = App.useApp();
  const navigate = useNavigate();
  const { current, loading } = useWmsWarehouse();
  const [mode, setMode] = useUrlFilter<Mode>("mode", "single", ["single", "order"]);
  const [waveId, setWaveId] = useUrlFilter<string>("wave", "");
  const queue = useQuery({
    queryKey: ["packing", current?.id],
    queryFn: () => unwrap(api.GET("/v1/warehouses/{warehouseID}/packing", { params: { path: { warehouseID: current!.id } } })),
    enabled: !!current,
    refetchInterval: usePoll(15_000),
  });
  const waves = queue.data?.waves ?? [];
  const recording = !!current?.recordPacking;
  // Recorded in this browser (uploaded), or by the agent on this laptop (kept there).
  const byAgent = current?.recorder === "agent";
  const browserRec = usePackingRecorder(recording && !byAgent, current?.name ?? "");
  const agentRec = useAgentRecorder(recording && byAgent, current?.id);
  const rec = byAgent ? agentRec : browserRec;
  const uploads = useVideoUploads();
  // The warehouse records, the recorder is not ready: ask once before a
  // parcel closes unrecorded, then not again until it is ready.
  const accepted = useRef(false);
  useEffect(() => {
    if (rec.ready) accepted.current = false;
  }, [rec.ready]);
  const confirm = useCallback((): Promise<boolean> => {
    if (!recording || rec.ready || accepted.current) return Promise.resolve(true);
    const why = byAgent && rec.error ? tDynamic(`wms.agent.${rec.error}`, "wms.video.notReady") : byAgent ? t("wms.video.notReady") : t("wms.video.noCamera");
    return new Promise((resolve) =>
      modal.confirm({
        title: t("wms.video.unrecordedTitle"),
        content: `${why} ${t("wms.video.unrecordedHelp")}`,
        okText: t("wms.video.packAnyway"),
        cancelText: t("common.cancel"),
        onOk: () => {
          accepted.current = true;
          resolve(true);
        },
        onCancel: () => resolve(false),
      }),
    );
  }, [recording, rec.ready, rec.error, byAgent, modal, t, tDynamic]);
  const tailed = useTailedRecorder(rec, uploads.push);
  const desk: Desk = { rec: recording ? tailed : null, push: uploads.push, confirm };
  const wave = waves.find((w) => w.id === waveId);

  if (!loading && !current) return <NoWms />;
  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("wms.pack.title")}
        </Typography.Title>
        <Space wrap>
          <Button onClick={() => navigate("/app/warehouse/packing/packagings")}>{t("wms.pkg.title")}</Button>
          <Button onClick={() => navigate("/app/warehouse/outbound")}>
            {t("wms.pack.awaitingHandover", { n: queue.data?.awaitingHandover ?? 0 })}
          </Button>
        </Space>
      </Flex>
      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
        <Segmented<Mode>
          value={mode}
          onChange={setMode}
          options={[
            { value: "single", label: t("wms.pack.modeSingle") },
            { value: "order", label: t("wms.pack.modeOrder") },
          ]}
        />
      </Flex>
      {recording && (byAgent ? <AgentCamera rec={agentRec} /> : <PackingCamera rec={browserRec} uploads={uploads} />)}
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={9}>
          <WaveQueue waves={waves} loading={queue.isPending} selected={waveId} onSelect={setWaveId} />
        </Col>
        <Col xs={24} xl={15}>
          {current && mode === "single" && <SingleDesk warehouse={current} wave={wave} desk={desk} />}
          {current && mode === "order" && <OrderDesk warehouseId={current.id} wave={wave} requireBox={current.requireBoxScan} desk={desk} />}
        </Col>
      </Row>
    </>
  );
}

function WaveQueue({ waves, loading, selected, onSelect }: { waves: PackingWave[]; loading: boolean; selected: string; onSelect: (id: string) => void }) {
  const { t } = useI18n();
  return (
    <Card size="small" title={t("wms.pack.queue")}>
      <Table<PackingWave>
        size="small"
        rowKey="id"
        loading={loading}
        dataSource={waves}
        pagination={false}
        locale={{ emptyText: t("wms.pack.queueEmpty") }}
        rowClassName={(w) => (w.id === selected ? "ant-table-row-selected" : "")}
        onRow={(w) => ({ onClick: () => onSelect(w.id === selected ? "" : w.id), style: { cursor: "pointer" } })}
        columns={[
          {
            title: t("wms.pack.wave"),
            dataIndex: "number",
            render: (v: string, w) => (
              <Space orientation="vertical" size={2}>
                <Typography.Text strong className="z-num">
                  {v}
                </Typography.Text>
                <Space size={2} wrap>
                  {w.methods.map((m) => (
                    <MethodTag key={m} method={m} />
                  ))}
                </Space>
              </Space>
            ),
          },
          {
            title: t("wms.pack.ready"),
            width: 90,
            align: "right",
            render: (_, w) => (
              <Typography.Text strong={w.picked > 0} className="z-num">
                {w.picked}
              </Typography.Text>
            ),
          },
          {
            title: t("wms.wave.order.packed"),
            width: 130,
            render: (_, w) => {
              const total = w.picking + w.picked + w.packed;
              return <Progress size="small" percent={Math.round((w.packed / Math.max(total, 1)) * 100)} format={() => `${w.packed}/${total}`} />;
            },
          },
        ]}
      />
      <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
        {t("wms.pack.queueHelp")}
      </Typography.Paragraph>
    </Card>
  );
}

/** Result of one packed parcel, shown big for the packer. */
function Packed({ order, onReprint, printing, onUnpacked }: { order: PackOrder; onReprint: () => void; printing: boolean; onUnpacked?: (o: PackOrder) => void }) {
  const { t, formatNumber } = useI18n();
  const parcel = order.parcel;
  return (
    <Card size="small" style={{ borderColor: "var(--ant-color-success)" }}>
      <Flex justify="space-between" align="start" wrap gap={12}>
        <Space orientation="vertical" size={4}>
          <Space>
            <CheckCircleFilled style={{ color: "var(--ant-color-success)", fontSize: 20 }} />
            <Typography.Text strong className="z-num" style={{ fontSize: 18 }}>
              {order.externalId}
            </Typography.Text>
            {order.slot != null && <Tag className="z-num">{t("wms.wave.slot", { n: order.slot })}</Tag>}
          </Space>
          <Typography.Text type="secondary">
            {t(`platform.${order.platform}`)} · {order.carrier || "—"} <CourierKindTag kind={order.courierKind} />
          </Typography.Text>
        </Space>
        <Space orientation="vertical" size={0} style={{ textAlign: "right" }}>
          <Typography.Text type="secondary">{t("wms.pack.box")}</Typography.Text>
          <Typography.Text strong className="z-num" style={{ fontSize: 24 }}>
            {parcel?.packagingCode || t("wms.pack.noBox")}
          </Typography.Text>
          {parcel && <Typography.Text className="z-num">{formatNumber(parcel.weightG)} g</Typography.Text>}
        </Space>
      </Flex>
      <Flex justify="space-between" align="center" wrap gap={8} style={{ marginTop: 12 }}>
        <MissingData order={order} />
        <Space wrap>
          {onUnpacked && parcel?.status === "packed" && <UnpackButton order={order} onDone={onUnpacked} />}
          <Button icon={<PrinterOutlined />} loading={printing} onClick={onReprint}>
            {t("wms.pack.reprint")}
          </Button>
        </Space>
      </Flex>
    </Card>
  );
}

/** "Batal kemas": open a packed parcel again, before it is handed over. */
function UnpackButton({ order, onDone }: { order: PackOrder; onDone: (o: PackOrder) => void }) {
  const { t } = useI18n();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const unpack = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/packing/orders/{orderID}/unpack", { params: { path: { orderID: order.orderId } } })),
    onSuccess: (res) => {
      queryClient.setQueryData(["packing", "order", order.orderId], res);
      for (const k of ["packing", "waves", "manifests"]) void queryClient.invalidateQueries({ queryKey: [k] });
      void message.success(t("wms.pack.unpacked", { number: res.externalId }));
      onDone(res);
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Button
      danger
      loading={unpack.isPending}
      onClick={() =>
        modal.confirm({
          title: t("wms.pack.unpack"),
          content: t("wms.pack.unpackConfirm", { number: order.externalId }),
          okButtonProps: { danger: true },
          okText: t("wms.pack.unpack"),
          cancelText: t("common.cancel"),
          onOk: () => unpack.mutateAsync(),
        })
      }
    >
      {t("wms.pack.unpack")}
    </Button>
  );
}

function MissingData({ order }: { order: PackOrder }) {
  const { t } = useI18n();
  if (order.noSize.length === 0 && order.noWeight.length === 0) return <span />;
  return (
    <Typography.Text type="warning" style={{ fontSize: 12 }}>
      <WarningOutlined />{" "}
      {[
        order.noSize.length > 0 ? t("wms.pack.noSize", { codes: order.noSize.join(", ") }) : "",
        order.noWeight.length > 0 ? t("wms.pack.noWeight", { codes: order.noWeight.join(", ") }) : "",
      ]
        .filter(Boolean)
        .join(" ")}
    </Typography.Text>
  );
}

/**
 * Single-pcs: the order that needs the scanned item is packed and its label
 * printed. Where the warehouse requires a box scan, the desk works as packers
 * do: the tote, then the item, then the packaging it goes into, and that
 * last scan closes the parcel. Otherwise the item scan packs at once, in the
 * suggested packaging or in one scanned earlier (it stays for the next parcels).
 */
function SingleDesk({ warehouse, wave, desk }: { warehouse: Schemas["Warehouse"]; wave?: PackingWave; desk: Desk }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const label = usePackLabel();
  const [recent, setRecent] = useState<PackOrder[]>([]);
  const [box, setBox] = useState<Packaging | null>(null);
  const [tote, setTote] = useState<Contents | null>(null);
  // Box scan required: the item waits here for its packaging.
  const [item, setItem] = useState<string | null>(null);
  const requireBox = warehouse.requireBoxScan;
  // One recording per parcel: running between packs, cut when one closes.
  const rec = desk.rec;
  const ready = rec?.ready ?? false;
  const recOn = rec?.recording ?? false;
  const start = rec?.start;
  const discard = rec?.discard;
  const tailing = rec?.tailing ?? false;
  const singleLabel = t("wms.video.singleLabel");
  // Waiting for the next item: a segment runs, to be the next parcel's clip.
  // Not during a parcel's tail: it starts once the tail ends.
  useEffect(() => {
    if (ready && !recOn && start && !tailing) start(singleLabel);
  }, [ready, recOn, start, tailing, singleLabel]);
  useEffect(() => () => discard?.(), [discard]);
  const boxes = useQuery({
    queryKey: ["packagings", warehouse.id],
    queryFn: () => unwrap(api.GET("/v1/warehouses/{warehouseID}/packagings", { params: { path: { warehouseID: warehouse.id } } })),
  });
  const pack = useMutation({
    mutationFn: ({ code, packaging }: { code: string; packaging: Packaging | null }) =>
      unwrap(
        api.POST("/v1/packing/single", {
          body: { warehouseId: warehouse.id, code, waveId: wave?.id ?? null, packagingId: packaging?.id ?? null, containerId: tote?.id ?? null },
        }),
      ),
    onSuccess: (o) => {
      if (rec) {
        // Packed while the last parcel's tail runs: that clip ends here and
        // this parcel gets its own. Else the waiting segment becomes its clip.
        if (rec.tailing) rec.start(o.externalId, o.orderId);
        else rec.setLabel(o.externalId);
        rec.finishAfter(o.orderId, o.externalId);
      }
      setRecent((r) => [o, ...r.filter((x) => x.orderId !== o.orderId)].slice(0, 20));
      setItem(null);
      for (const k of ["packing", "waves", "tasks"]) void queryClient.invalidateQueries({ queryKey: [k] });
      void label.print(o.orderId, o.externalId);
    },
    onError: (err) => {
      // The item is not packable (no order needs it, wrong tote): scan again.
      setItem(null);
      void message.error(errorText(err));
    },
  });
  const scan = (code: string): ScanResult => {
    if (CONTAINER.test(code)) {
      // A tote: pack only what is in it.
      return scanContainer(warehouse.id, code).then(
        (c) => {
          setTote(c);
          return true;
        },
        (err: unknown) => {
          void message.error(errorText(err));
          throw err;
        },
      );
    }
    const hit = (boxes.data?.items ?? []).find((p) => p.active && p.code === code.toUpperCase());
    if (requireBox) {
      if (hit) {
        if (!item) {
          void message.warning(t("wms.pack.scanItemFirst"));
          return false;
        }
        // The packaging closes the parcel.
        return desk.confirm().then((ok): unknown => (ok ? pack.mutateAsync({ code: item, packaging: hit }) : false));
      }
      setItem(code);
      return true;
    }
    if (hit) {
      setBox(hit);
      return true;
    }
    return desk.confirm().then((ok): unknown => (ok ? pack.mutateAsync({ code, packaging: box }) : false));
  };
  const [last, ...earlier] = recent;
  return (
    <Space orientation="vertical" size={12} style={{ width: "100%" }}>
      <Card size="small" title={wave ? t("wms.pack.singleInWave", { number: wave.number }) : t("wms.pack.singleAll")}>
        <Space orientation="vertical" size={8} style={{ width: "100%" }}>
          <Typography.Text type="secondary">{t(requireBox ? "wms.pack.singleHelpBox" : "wms.pack.singleHelp")}</Typography.Text>
          <ScanInput onScan={scan} disabled={pack.isPending} placeholder={t(requireBox && item ? "wms.pack.scanBox" : "wms.pack.scanItem")} />
          {tote && (
            <Space wrap>
              <Typography.Text type="secondary">{t("wms.pack.fromTote")}:</Typography.Text>
              <Tag color="processing" closable onClose={() => setTote(null)} className="z-num" style={{ fontSize: 14 }}>
                {tote.code}
              </Tag>
            </Space>
          )}
          {requireBox && (
            <Space wrap>
              <Typography.Text type="secondary">{t("wms.pack.item")}:</Typography.Text>
              {item ? (
                <Tag color="processing" closable onClose={() => setItem(null)} className="z-num" style={{ fontSize: 14 }}>
                  {item}
                </Tag>
              ) : (
                <Typography.Text type="secondary">{t("wms.pack.boxNeeded")}</Typography.Text>
              )}
            </Space>
          )}
          <Space wrap>
            <Typography.Text type="secondary">{t("wms.pack.box")}:</Typography.Text>
            {requireBox ? (
              <Typography.Text type={item ? "warning" : "secondary"}>{t(item ? "wms.pack.boxNow" : "wms.pack.boxAfterItem")}</Typography.Text>
            ) : box ? (
              <Tag color="blue" closable onClose={() => setBox(null)} className="z-num" style={{ fontSize: 14 }}>
                {box.code}
              </Tag>
            ) : (
              <Typography.Text type="secondary">{t("wms.pack.boxSuggestedEach")}</Typography.Text>
            )}
          </Space>
        </Space>
      </Card>
      {last && (
        <Packed
          order={last}
          printing={label.printing === last.orderId}
          onReprint={() => void label.print(last.orderId, last.externalId)}
          onUnpacked={(o) => setRecent((r) => r.filter((x) => x.orderId !== o.orderId))}
        />
      )}
      {earlier.length > 0 && (
        <Card size="small" title={t("wms.pack.recent")}>
          <List
            size="small"
            dataSource={earlier}
            renderItem={(o) => (
              <List.Item
                actions={[
                  <Button key="print" size="small" type="link" loading={label.printing === o.orderId} onClick={() => void label.print(o.orderId, o.externalId)}>
                    {t("wms.pack.reprint")}
                  </Button>,
                ]}
              >
                <Space>
                  <span className="z-num">{o.externalId}</span>
                  <Tag className="z-num">{o.parcel?.packagingCode || t("wms.pack.noBox")}</Tag>
                </Space>
              </List.Item>
            )}
          />
        </Card>
      )}
    </Space>
  );
}

/** Batch and cluster: the wave's picked orders, one opened at a time. */
function OrderDesk({ warehouseId, wave, requireBox, desk }: { warehouseId: string; wave?: PackingWave; requireBox: boolean; desk: Desk }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const [openId, setOpenId] = useState<string | null>(null);
  // Orders still in the tote last scanned: the next one opens after each pack.
  const [toteOrders, setToteOrders] = useState<string[]>([]);
  const detail = useQuery({
    queryKey: ["waves", "detail", wave?.id],
    queryFn: () => unwrap(api.GET("/v1/waves/{waveID}", { params: { path: { waveID: wave!.id } } })),
    enabled: !!wave,
    refetchInterval: usePoll(15_000),
  });
  const orders = (detail.data?.orderList ?? []).filter((o) => o.status !== "removed");
  const ready = orders.filter((o) => o.status === "picked");

  const find = (code: string): ScanResult => {
    const c = code.trim().toUpperCase();
    if (CONTAINER.test(c)) {
      // A cart slot opens its order; a tote, its first order (then the next).
      return scanContainer(warehouseId, c).then(
        (box) => {
          const ids = box.orders.map((o) => o.orderId);
          if (ids.length === 0) {
            void message.warning(t("wms.pack.toteEmpty", { code: box.code }));
            return false;
          }
          setToteOrders(ids.slice(1));
          setOpenId(ids[0]!);
          return true;
        },
        (err: unknown) => {
          void message.error(errorText(err));
          throw err;
        },
      );
    }
    const slot = /^S?(\d{1,3})$/.exec(c);
    const hit = ready.find((o) => o.externalId.toUpperCase() === c || (slot && o.slot === Number(slot[1])));
    if (hit) {
      setOpenId(hit.orderId);
      return true;
    }
    void message.warning(t("wms.pack.notReady", { code }));
    return false;
  };
  const next = (after: WaveOrder | undefined) => {
    if (toteOrders.length > 0) {
      setOpenId(toteOrders[0]!);
      setToteOrders((x) => x.slice(1));
      return;
    }
    // Batch: the next order of the same contents, then any.
    const rest = ready.filter((o) => o.orderId !== after?.orderId);
    const same = after ? rest.find((o) => o.group && o.group === after.group) : undefined;
    setOpenId((same ?? rest[0])?.orderId ?? null);
  };

  if (openId) {
    const current = orders.find((o) => o.orderId === openId);
    return (
      <PackPanel
        orderId={openId}
        requireBox={requireBox}
        desk={desk}
        onClose={() => {
          setOpenId(null);
          setToteOrders([]);
        }}
        onPacked={() => {
          if (wave) void detail.refetch();
          next(current);
        }}
      />
    );
  }
  return (
    <Card size="small" title={wave ? t("wms.pack.ordersOf", { number: wave.number }) : t("wms.pack.scanTitle")}>
      <Space orientation="vertical" size={8} style={{ width: "100%" }}>
        <ScanInput onScan={find} placeholder={t("wms.pack.scanOrder")} />
        {!wave && <Empty description={t("wms.pack.pickWaveOrScan")} style={{ marginTop: 24 }} />}
        <Flex justify="space-between" align="center">
          <Typography.Text type="secondary">{t("wms.pack.readyCount", { n: ready.length })}</Typography.Text>
          <Button type="primary" disabled={ready.length === 0} onClick={() => next(undefined)}>
            {t("wms.pack.next")}
          </Button>
        </Flex>
        <Table<WaveOrder>
          size="small"
          rowKey="orderId"
          loading={detail.isPending}
          dataSource={orders}
          pagination={orders.length > 50 ? { pageSize: 50, showSizeChanger: false } : false}
          onRow={(o) => ({ onClick: () => o.status === "picked" && setOpenId(o.orderId), style: { cursor: o.status === "picked" ? "pointer" : "default" } })}
          columns={[
            {
              title: t("orders.number"),
              dataIndex: "externalId",
              render: (v: string, o) => (
                <Space size={4}>
                  <span className="z-num">{v}</span>
                  {o.slot != null && <Tag className="z-num">{t("wms.wave.slot", { n: o.slot })}</Tag>}
                </Space>
              ),
            },
            { title: t("wms.pool.method"), dataIndex: "method", width: 110, render: (m: string) => <MethodTag method={m} /> },
            { title: t("wms.wave.units"), dataIndex: "units", width: 70, align: "right" },
            { title: t("orders.shipBy"), dataIndex: "shipBy", width: 130, render: (x: string | null) => <ShipBy at={x} /> },
            { title: t("common.status"), dataIndex: "status", width: 120, render: (s: string) => <WaveOrderTag status={s} /> },
          ]}
        />
      </Space>
    </Card>
  );
}

type Scanned = Record<string, { qty: number; serials: string[] }>;

/**
 * One order at the desk: scan each item, then the packaging's barcode (or
 * Enter for the suggestion, unless the warehouse requires the scan). The
 * parcel closes and its label prints as soon as both are done.
 */
function PackPanel({ orderId, requireBox, desk, onClose, onPacked }: { orderId: string; requireBox: boolean; desk: Desk; onClose: () => void; onPacked: () => void }) {
  const { t, formatNumber } = useI18n();
  const { can } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const label = usePackLabel();
  const [scanned, setScanned] = useState<Scanned>({});
  // A serial item's barcode was scanned: the next scan is its IMEI.
  const [awaitSerial, setAwaitSerial] = useState<string | null>(null);
  const [box, setBox] = useState<string | null | undefined>(undefined);
  const [boxScanned, setBoxScanned] = useState(false);
  const order = useQuery({
    queryKey: ["packing", "order", orderId],
    queryFn: () => unwrap(api.GET("/v1/packing/orders/{orderID}", { params: { path: { orderID: orderId } } })),
  });
  const o = order.data;
  // Recorded from the moment the order is open until its parcel closes.
  const rec = desk.rec;
  const ready = rec?.ready ?? false;
  const start = rec?.start;
  const discard = rec?.discard;
  const ext = o?.externalId;
  const packable = !!o && !o.parcel && o.status === "picked";
  useEffect(() => {
    if (ready && ext && packable && start) start(ext, orderId);
  }, [ready, ext, packable, start, orderId]);
  useEffect(() => () => discard?.(), [discard]);
  const chosen = box === undefined ? (requireBox ? null : (o?.suggestedId ?? null)) : box;
  const pack = useMutation({
    mutationFn: ({ packagingId, items }: { packagingId: string | null; items: Scanned }) =>
      unwrap(
        api.POST("/v1/packing/orders/{orderID}/pack", {
          params: { path: { orderID: orderId } },
          body: {
            packagingId,
            items: (o?.lines ?? []).map((l) =>
              l.tracking === "serial" ? { skuId: l.skuId, serials: items[l.skuId]?.serials ?? [] } : { skuId: l.skuId, quantity: items[l.skuId]?.qty ?? 0 },
            ),
          },
        }),
      ),
    onSuccess: async (res) => {
      // The clip runs on while the label goes on (TAIL_MS), or until the next order opens.
      if (rec) rec.finishAfter(res.orderId, res.externalId);
      queryClient.setQueryData(["packing", "order", orderId], res);
      for (const k of ["packing", "waves"]) void queryClient.invalidateQueries({ queryKey: [k] });
      void message.success(t("wms.pack.packed", { number: res.externalId }));
      await label.print(res.orderId, res.externalId);
      onPacked();
    },
    onError: (err) => void message.error(errorText(err)),
  });

  if (!o) return <Card size="small" loading />;
  const doneOf = (l: PackOrder["lines"][number], s: Scanned) => (l.tracking === "serial" ? (s[l.skuId]?.serials.length ?? 0) : (s[l.skuId]?.qty ?? 0));
  const completeWith = (s: Scanned) => o.lines.length > 0 && o.lines.every((l) => doneOf(l, s) === l.quantity);
  const complete = completeWith(scanned);
  const busy = pack.isPending || label.printing === o.orderId;
  const close = (packagingId: string | null, items: Scanned): ScanResult => {
    if (!busy && o.status === "picked") return desk.confirm().then((ok): unknown => (ok ? pack.mutateAsync({ packagingId, items }) : false));
    return false;
  };
  const suggested = o.options.find((p) => p.id === o.suggestedId);

  const warn = (text: string) => {
    void message.warning(text);
    return false;
  };
  const scan = (code: string): ScanResult => {
    const up = code.toUpperCase();
    const pkg = o.options.find((p) => p.code === up);
    if (pkg) {
      setBox(pkg.id);
      setBoxScanned(true);
      return complete ? close(pkg.id, scanned) : true;
    }
    let next: Scanned;
    const line = o.lines.find((l) => l.skuCode.toUpperCase() === up || l.barcode === code);
    // An IMEI: named for the item whose barcode was just scanned, the item
    // it was picked with, or the only serial item still short (Plan §6.21).
    const open = o.lines.filter((l) => l.tracking === "serial" && (scanned[l.skuId]?.serials.length ?? 0) < l.quantity);
    const forSerial = line
      ? undefined
      : (o.lines.find((l) => l.skuId === awaitSerial) ?? o.lines.find((l) => l.serials.includes(code)) ?? (open.length === 1 ? open[0] : undefined));
    if (forSerial) {
      const have = scanned[forSerial.skuId]?.serials ?? [];
      if (have.includes(code)) return warn(t("wms.pack.serialTwice", { serial: code }));
      if (have.length >= forSerial.quantity) return warn(t("wms.pack.tooMany", { code: forSerial.skuCode }));
      next = { ...scanned, [forSerial.skuId]: { qty: have.length + 1, serials: [...have, code] } };
      setAwaitSerial(null);
    } else {
      if (!line) return warn(open.length > 1 ? t("wms.pack.scanItemBeforeSerial") : t("wms.pack.notInOrder", { code }));
      if (line.tracking === "serial") {
        if ((scanned[line.skuId]?.serials.length ?? 0) >= line.quantity) return warn(t("wms.pack.tooMany", { code: line.skuCode }));
        setAwaitSerial(line.skuId);
        void message.info(t("wms.pack.scanSerial", { code: line.skuCode }));
        return true;
      }
      const have = scanned[line.skuId]?.qty ?? 0;
      if (have >= line.quantity) return warn(t("wms.pack.tooMany", { code: line.skuCode }));
      next = { ...scanned, [line.skuId]: { qty: have + 1, serials: [] } };
    }
    setScanned(next);
    // The box was scanned first: the last item closes the parcel.
    if (completeWith(next) && boxScanned) return close(chosen, next);
    return true;
  };
  const enter = (): ScanResult => {
    if (!complete) return undefined;
    if (requireBox && !boxScanned) return warn(t("wms.pack.scanBoxFirst"));
    return close(chosen, scanned);
  };

  if (o.parcel) {
    return (
      <Space orientation="vertical" size={12} style={{ width: "100%" }}>
        <Packed
          order={o}
          printing={label.printing === o.orderId}
          onReprint={() => void label.print(o.orderId, o.externalId)}
          onUnpacked={() => {
            setScanned({});
            setBox(undefined);
            setBoxScanned(false);
          }}
        />
        <Button onClick={onClose}>{t("common.back")}</Button>
      </Space>
    );
  }
  return (
    <Card
      size="small"
      title={
        <Space wrap>
          <span className="z-num">{o.externalId}</span>
          {o.slot != null && <Tag color="purple" className="z-num">{t("wms.wave.slot", { n: o.slot })}</Tag>}
          {o.method && <MethodTag method={o.method} />}
        </Space>
      }
      extra={<Button type="text" onClick={onClose}>{t("common.back")}</Button>}
    >
      <Space orientation="vertical" size={12} style={{ width: "100%" }}>
        <Typography.Text type="secondary">
          {t(`platform.${o.platform}`)} · {o.carrier || "—"} · <ShipBy at={o.shipBy} /> <CourierKindTag kind={o.courierKind} />
        </Typography.Text>
        {o.status !== "picked" && (
          <Alert
            type="warning"
            showIcon
            title={o.waitingZones.length > 0 ? t("wms.pack.waitingZones", { zones: o.waitingZones.join(", ") }) : t("wms.pack.notPicked")}
          />
        )}
        <ScanInput onScan={scan} onEmptyEnter={enter} disabled={busy} placeholder={t(complete ? "wms.pack.scanBox" : "wms.pack.scanItem")} />
        {complete && !busy && (
          <Alert
            type="info"
            showIcon
            title={
              requireBox
                ? t("wms.pack.promptBoxRequired")
                : suggested
                  ? t("wms.pack.promptBox", { code: suggested.code })
                  : t("wms.pack.promptBoxNoSuggestion")
            }
          />
        )}
        <Table
          size="small"
          rowKey="skuId"
          dataSource={o.lines}
          pagination={false}
          columns={[
            {
              title: t("catalog.code"),
              dataIndex: "skuCode",
              render: (v: string, l) => (
                <Space align="start">
                <SkuThumb skuId={l.skuId} size={56} />
                <Space orientation="vertical" size={0}>
                  <Typography.Text strong className="z-num">
                    {v}
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {l.skuName}
                  </Typography.Text>
                  {l.tracking === "serial" && (scanned[l.skuId]?.serials.length ?? 0) > 0 && (
                    <Space size={2} wrap>
                      {scanned[l.skuId]!.serials.map((sn) => (
                        <Tag
                          key={sn}
                          className="z-num"
                          closable
                          onClose={() =>
                            setScanned((s) => {
                              const left = (s[l.skuId]?.serials ?? []).filter((x) => x !== sn);
                              return { ...s, [l.skuId]: { qty: left.length, serials: left } };
                            })
                          }
                        >
                          {sn}
                        </Tag>
                      ))}
                    </Space>
                  )}
                </Space>
                </Space>
              ),
            },
            {
              title: t("wms.pack.checked"),
              width: 140,
              align: "right",
              render: (_, l) => {
                const n = doneOf(l, scanned);
                return (
                  <Typography.Text strong className="z-num" type={n === l.quantity ? "success" : undefined} style={{ fontSize: 18 }}>
                    {n} / {l.quantity}
                  </Typography.Text>
                );
              },
            },
          ]}
        />
        <div>
          <Typography.Text strong>{t("wms.pack.box")}</Typography.Text>{" "}
          <Typography.Text type="secondary">
            · {t("wms.pack.weight", { g: formatNumber(o.weightG) })}
            {requireBox ? ` · ${t("wms.pack.boxByScan")}` : ""}
          </Typography.Text>
          {o.options.length === 0 ? (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 8 }}
              title={t("wms.pkg.none")}
              action={can("warehouse.manage") ? <Button size="small" href="/app/warehouse/packing/packagings">{t("wms.pkg.add")}</Button> : undefined}
            />
          ) : (
            <Radio.Group
              value={chosen}
              disabled={requireBox}
              onChange={(e) => {
                setBox(e.target.value as string);
                setBoxScanned(false);
              }}
              style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}
            >
              {o.options.map((p) => (
                <Radio key={p.id} value={p.id}>
                  <Space size={6} wrap>
                    <Typography.Text strong className="z-num">
                      {p.code}
                    </Typography.Text>
                    <Typography.Text type="secondary" className="z-num">
                      {p.lengthMm}×{p.widthMm}×{p.heightMm}
                    </Typography.Text>
                    {p.id === o.suggestedId && <Tag color="green">{t("wms.pack.suggested")}</Tag>}
                    {p.id === chosen && boxScanned && <Tag color="blue">{t("wms.pack.scanned")}</Tag>}
                    {!p.fits && <Tag color="warning">{t(`wms.pack.reason.${p.reason ?? "too_small"}`, { code: p.tooBig ?? "" })}</Tag>}
                  </Space>
                </Radio>
              ))}
            </Radio.Group>
          )}
          {o.options.length > 0 && !o.suggestedId && <Alert type="warning" showIcon style={{ marginTop: 8 }} title={t("wms.pack.nothingFits")} />}
          <div style={{ marginTop: 8 }}>
            <MissingData order={o} />
          </div>
        </div>
        <Flex gap={8} wrap>
          <Button type="primary" size="large" disabled={!complete || o.status !== "picked" || (requireBox && !boxScanned)} loading={busy} onClick={() => close(chosen, scanned)}>
            {t("wms.pack.close")}
          </Button>
          <Button
            onClick={() => {
              setScanned({});
              setBox(undefined);
              setBoxScanned(false);
            }}
            disabled={Object.keys(scanned).length === 0 && !boxScanned}
          >
            {t("wms.pack.restart")}
          </Button>
        </Flex>
      </Space>
    </Card>
  );
}
